import { useCallback, useState } from 'react';
import type { ChangeEvent } from 'react';
import { toast } from 'sonner';
import type { CsvUploadResult } from './model';

interface CsvUploadResponse<TResult extends CsvUploadResult> {
  success: boolean;
  data?: TResult;
  error?: string;
}

interface RunCsvUploadOptions<TResult extends CsvUploadResult> {
  upload: (csvContent: string) => Promise<CsvUploadResponse<TResult>>;
  onSuccess: (result: TResult) => void | Promise<void>;
  failureMessage: string;
  unexpectedMessage: string;
}

export function useCsvImport<TResult extends CsvUploadResult = CsvUploadResult>() {
  const [csvContent, setCsvContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<TResult | null>(null);

  const onFileSelect = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      setCsvContent(String(loadEvent.target?.result ?? ''));
    };
    reader.readAsText(file);
  }, []);

  const runUpload = useCallback(
    async ({
      upload,
      onSuccess,
      failureMessage,
      unexpectedMessage,
    }: RunCsvUploadOptions<TResult>) => {
      if (!csvContent.trim()) {
        toast.error('Please select a CSV file first');
        return;
      }

      setIsUploading(true);
      setResult(null);

      try {
        const response = await upload(csvContent);
        if (response.success && response.data) {
          setResult(response.data);
          await onSuccess(response.data);
        } else {
          toast.error(response.error || failureMessage);
        }
      } catch (error) {
        console.error('CSV upload error:', error);
        toast.error(unexpectedMessage);
      } finally {
        setIsUploading(false);
      }
    },
    [csvContent]
  );

  const reset = useCallback(() => {
    setCsvContent('');
    setFileName('');
    setResult(null);
    setIsUploading(false);
  }, []);

  return {
    csvContent,
    fileName,
    isUploading,
    result,
    onFileSelect,
    runUpload,
    reset,
  };
}
